import { SCROLL_CLASS_NAMES } from "../../models/scroll/ScrollHTMLClass";
import { ScrollMarkRule } from "../../models/scroll/ScrollTriggerRule";
import { StringContext } from "../StringContext";

/**
 * Base class for managing scroll behavior in the system.
 * Handles abstract scroll state and updates, intended for extension.
 */
export class ScrollController {
  /** Shared context containing data and tools */
  protected context: StringContext;

  /** Reference to the document object */
  protected document: Document;

  /** Name of the scroll mode (e.g. 'default', 'smooth', etc.) */
  public name: string = "";

  /** Whether the system is in programmatic scroll mode */
  public isProg: boolean = false;

  /** Whether parallax-related logic should be active */
  public isParallaxEnabled: boolean = false;

  /** Scroll direction: vertical or horizontal */
  protected _scrollDirection: "vertical" | "horizontal" = "vertical";

  /**
   * Current scroll direction.
   * - `true` — scrolling down
   * - `false` — scrolling up
   * - `null` — unknown (initial state)
   */
  protected isBottomScrollDirection: boolean | null = null;

  protected isLastBottomScrollDirection: boolean = true;

  protected scrollTriggerRules: Array<ScrollMarkRule> = [];

  /** Tracks whether this controller is currently active */
  protected isActive: boolean = false;

  /** Last direction applied to DOM classes (for global-class toggles) */
  protected lastAppliedDirection: boolean | null = null;

  /**
   * Sets scroll direction and updates internal scroll logic.
   * @param scrollDirection Either 'vertical' or 'horizontal'.
   */
  public set scrollDirection(scrollDirection: "vertical" | "horizontal") {
    this._scrollDirection = scrollDirection;

    if (this._scrollDirection === "vertical") {
      this.onCalcUpdate = () => {
        if (!this.isActive) return;
        this.context.data.scroll.scrollContainer?.scrollTo(0, this.context.data.scroll.current);
        this.triggerScrollRules();
      };
    } else if (this._scrollDirection === "horizontal") {
      this.onCalcUpdate = () => {
        if (!this.isActive) return;
        this.context.data.scroll.scrollContainer?.scrollTo(this.context.data.scroll.current, 0);
      };
    }
  }

  /**
   * Creates a new ScrollController instance.
   * @param context Shared context containing data and settings.
   */
  constructor(context: StringContext) {
    this.document = document;
    this.context = context;
  }

  /**
   * Called when scroll direction changes (up ↔ down).
   * Override this callback in subclasses or instances.
   */
  public onChangeDirection = () => {};

  /**
   * Called when scroll starts (user input).
   * Override this callback in subclasses or instances.
   */
  public onScrollStart = () => {};

  /**
   * Called when scroll ends.
   * Override this callback in subclasses or instances.
   */
  public onScrollStop = () => {};

  /**
   * Scroll-to function called on each frame.
   * This will be reassigned depending on scroll direction.
   */
  public onCalcUpdate: () => void = () => {
    if (!this.isActive) return;
    this.context.data.scroll.scrollContainer?.scrollTo(0, this.context.data.scroll.current);
    this.triggerScrollRules();
  };

  /**
   * Called every animation frame.
   * Intended to be overridden in subclasses.
   */
  public onFrame(): void {}

  /**
   * Called when wheel event is fired.
   * Override to implement custom scroll interaction.
   * @param e Wheel event.
   */
  public onWheel(e: any): void {}

  /**
   * Called when native scroll event is fired.
   * Override to track native scroll position.
   * @param e Scroll event.
   */
  public onScroll(e: any): void {}

  public disableScrollEvents(): void {}

  public enableScrollEvents(): void {}

  /**
   * Called when this controller becomes the active scroll engine.
   * Ensures event bindings are attached only once.
   */
  public activate(): void {
    if (this.isActive) return;
    this.isActive = true;
    this.enableScrollEvents();
  }

  /**
   * Called when this controller is deactivated.
   * Ensures event bindings are detached only once.
   */
  public deactivate(): void {
    if (!this.isActive) return;
    this.isActive = false;
    this.disableScrollEvents();
    this.isProg = false;
    this.context.data.scroll.target = this.context.data.scroll.current;
    this.context.data.scroll.delta = 0;
    this.context.data.scroll.lerped = 0;
    this.context.data.scroll.displacement = 0;
    this.clearScrollingClasses();
    this.isBottomScrollDirection = null;
    this.lastAppliedDirection = null;
    this.onScrollStop();
  }

  /**
   * Allows controllers to clean up resources on global destroy.
   */
  public destroy(): void {}

  /**
   * Updates scroll direction state, emits events, and toggles global classes.
   * Intended for reuse by custom scroll adapters.
   */
  protected updateScrollDirection(newDirection: boolean) {
    this.isLastBottomScrollDirection = newDirection;
    if (this.isBottomScrollDirection === null) {
      this.isBottomScrollDirection = newDirection;
      return;
    }

    this.context.data.scroll.isScrollingDown = newDirection;
    this.onChangeDirection();
    this.context.events.emit(`scroll:direction:change`, this.context.data.scroll.isScrollingDown);

    if (this.context.settings["global-class"] && this.lastAppliedDirection !== newDirection) {
      document.documentElement.classList.toggle(SCROLL_CLASS_NAMES.SCROLLING_FORWARD, newDirection);
      document.documentElement.classList.toggle(
        SCROLL_CLASS_NAMES.SCROLLING_BACKWARD,
        !newDirection,
      );
      document.documentElement.classList.toggle(SCROLL_CLASS_NAMES.SCROLL_FORWARD, newDirection);
      document.documentElement.classList.toggle(SCROLL_CLASS_NAMES.SCROLL_BACKWARD, !newDirection);

      this.lastAppliedDirection = newDirection;
    }
  }

  protected clearScrollingClasses() {
    document.documentElement.classList.remove(
      SCROLL_CLASS_NAMES.SCROLLING_BACKWARD,
      SCROLL_CLASS_NAMES.SCROLLING_FORWARD,
    );
  }

  protected triggerScrollRules() {
    this.scrollTriggerRules.forEach((rule) => {
      const shouldTrigger =
        (rule.direction === "any" ||
          (this.isLastBottomScrollDirection && rule.direction === "forward")) &&
        this.context.data.scroll.current >= rule.offset;

      if (shouldTrigger && !rule.isActive) {
        // Activate
        rule.onEnter?.();
        if (rule.toggleClass) {
          rule.toggleClass.target.classList.add(rule.toggleClass.className);
        }
        rule.isActive = true;
      } else if (!shouldTrigger && rule.isActive) {
        // Deactivate
        rule.onLeave?.();
        if (rule.toggleClass) {
          rule.toggleClass.target.classList.remove(rule.toggleClass.className);
        }
        rule.isActive = false;
      }
    });
  }

  public addScrollMark(rule: ScrollMarkRule) {
    this.scrollTriggerRules.push(rule);
  }

  public removeScrollMark(id: string) {
    this.scrollTriggerRules = this.scrollTriggerRules.filter((rule) => rule.id !== id);
  }

  public scrollTo(position: number, immediate?: boolean) {}
}
